import { useState } from "react";
import { Box, Center, Flex, Tag, useToast, Text, Button, useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useAuth, WithAuthorization } from "../../context";
import {
  supplierQuestionnaireKeys,
  useGetSupplierQuestionnaireById,
  useRevertQuestionnaireCategory,
  useSetQuestionnaireCategory,
} from "../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import {
  QUESTIONNAIRE_STATUSES,
  showErrorToast,
  SUPPLIER_QUESTIONNAIRE_TYPES,
  SUPPLIER_TYPES,
  USER_ROLES,
} from "../../utils";
import { isAllowedToView } from "utils/AuthUtils";
import { SelectCategory } from "./components/SelectCategory";
import { SUPPLIER_QUESTIONNAIRE_CATEGORIES } from "./helpers";
import { Product } from "./components/Product";
import { ConsumptionBasedRevenue, Revenue } from "./components/Revenue";
import { Location } from "./components/Location";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import { useLocation, useNavigate } from "react-router-dom";
import { ReturnToList } from "./components/ReturnToList";
import * as React from "react";
import { AssigneeHeader } from "../../components/AssigneeHeader";
import { getSupplierQuestionnairesRoute } from "../SupplierQuestionnaires/helpers";
import { routes } from "../../config";
import { DecarbonizationNetZeroPlan } from "./components/DecarbonizationNetZeroPlan";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { useQueryClient } from "react-query";

function SupplierQuestionnaire() {
  const { t } = useTranslation("translation");
  const location = useLocation();
  const toast = useToast();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userPermissions } = useAuth();
  const queryClient = useQueryClient();
  const assignee = {
    name: user.name,
    avatarUrl: user.picture,
  };
  const [questionnaireData, setQuestionnaireData] = useState();
  const [submitAndReturnToList, setSubmitAndReturnToList] = useState(false);

  const { isOpen: isGetBackDialogOpen, onOpen: onGetBackDialogOpen, onClose: onGetBackDialogClose } = useDisclosure();

  const { data, isLoading, isFetching, error } = useGetSupplierQuestionnaireById(
    location.search.split("id=")[1] ?? null,
    {
      enabled:
        location &&
        location.search !== "" &&
        !isNaN(location.search.split("id=")[1]) &&
        location.search.split("id=")[1] !== null,
      onSuccess: (response) => {
        setQuestionnaireData(response);
      },
      onError: (err) => (err.response?.status !== 404 ? showErrorToast(toast, err.message) : null),
      onSettled: () => dispatch(hideAppSpinner()),
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const executeNavigation = () => {
    if (
      assignee?.name?.toLowerCase() === questionnaireData?.assigneeName?.toLowerCase() ||
      (assignee?.name?.toLowerCase() !== questionnaireData?.assigneeName?.toLowerCase() &&
        !isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) ||
      (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER))
    ) {
      // The assignee of the questionnaire is editing it or colleague of the assignee is reviewing it, so we are
      // returning to 'Supplier Questionnaires' page
      navigate(getSupplierQuestionnairesRoute(userPermissions));
    } else {
      // Admin is editing someone else's questionnaire, so we are returning to 'Data entry planning' page
      navigate(routes.supplierPortalDataEntryPlanning);
    }
  };

  const handleReturnToListClick = () => {
    if (
      questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED ||
      (questionnaireData.assigneeName?.toLowerCase() !== user.email?.toLowerCase() &&
        !isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) ||
      (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER)) ||
      !questionnaireData.category ||
      (questionnaireData.category === SUPPLIER_QUESTIONNAIRE_CATEGORIES.PRODUCT &&
        questionnaireData.products?.length === 0) ||
      (questionnaireData.category === SUPPLIER_QUESTIONNAIRE_CATEGORIES.LOCATION &&
        questionnaireData.locations?.length === 0)
    ) {
      executeNavigation();
    } else {
      setSubmitAndReturnToList(true);
    }
  };

  const handleNavigationToList = () => {
    setSubmitAndReturnToList(false);
    executeNavigation();
  };

  const { mutate: setCategory } = useSetQuestionnaireCategory({
    onSuccess: (response) => {
      setQuestionnaireData(response);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleSetCategory = (selectedCategory) => {
    if (selectedCategory !== null) {
      dispatch(showAppSpinner());
      setCategory({ id: questionnaireData.id, category: selectedCategory });
    }
  };

  const { mutate: revertCategoryChoice } = useRevertQuestionnaireCategory({
    onSuccess: () => {
      onGetBackDialogClose();
      queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleCategorySwitch = () => {
    revertCategoryChoice({ id: questionnaireData?.id, category: questionnaireData?.category });
  };

  const renderCategoryComponent = () => {
    const isPreview =
      questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED ||
      (questionnaireData.assigneeName?.toLowerCase() !== user.email?.toLowerCase() &&
        !isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) ||
      (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER));
    let categoryComponent;
    let translationPath = "";

    if (questionnaireData.questionnaireType === SUPPLIER_QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN) {
      translationPath = "supplierQuestionnaire.decarbonizationNetZeroPlan";
      categoryComponent = (
        <DecarbonizationNetZeroPlan
          data={questionnaireData}
          isPreview={isPreview}
          returnToListRequested={submitAndReturnToList}
          declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
          returnToList={handleNavigationToList}
        />
      );
    } else {
      switch (questionnaireData.category) {
        case SUPPLIER_QUESTIONNAIRE_CATEGORIES["PRODUCT"]:
          translationPath = "supplierQuestionnaire.product";
          categoryComponent = (
            <Product
              data={questionnaireData}
              returnToListRequested={submitAndReturnToList}
              declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
              returnToList={handleNavigationToList}
              isPreview={isPreview}
            />
          );
          break;
        case SUPPLIER_QUESTIONNAIRE_CATEGORIES["REVENUE"]:
          let revenueType = "";
          if (questionnaireData.questionnaireType === SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED) {
            revenueType = "emissionBasedRevenue";
            categoryComponent = (
              <Revenue
                data={{ ...questionnaireData, ...questionnaireData.revenue }}
                submitAndReturnToList={submitAndReturnToList}
                setSubmitAndReturnToList={setSubmitAndReturnToList}
                handleNavigationToList={handleNavigationToList}
                isPreview={isPreview}
              />
            );
          } else if (questionnaireData.questionnaireType === SUPPLIER_QUESTIONNAIRE_TYPES.CONSUMPTION_BASED) {
            revenueType = "consumptionBasedRevenue";
            categoryComponent = (
              <ConsumptionBasedRevenue
                data={{ ...questionnaireData, ...questionnaireData.revenue }}
                submitAndReturnToList={submitAndReturnToList}
                setSubmitAndReturnToList={setSubmitAndReturnToList}
                handleNavigationToList={handleNavigationToList}
                isPreview={isPreview}
              />
            );
          }
          translationPath = `supplierQuestionnaire.${revenueType}`;
          break;
        case SUPPLIER_QUESTIONNAIRE_CATEGORIES["LOCATION"]:
          translationPath = "supplierQuestionnaire.location";
          categoryComponent = (
            <Location
              data={questionnaireData}
              returnToListRequested={submitAndReturnToList}
              declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
              returnToList={handleNavigationToList}
              isPreview={isPreview}
            />
          );
          break;
        default:
          translationPath = "supplierQuestionnaire.selectCategory";
          categoryComponent =
            (questionnaireData.assigneeName?.toLowerCase() !== user.email?.toLowerCase() &&
              !isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) ||
            (questionnaireData.assigneeName?.toLowerCase() === user.email?.toLowerCase() &&
              userPermissions?.length === 1 &&
              userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER)) ? (
              <Text fontSize="lg" fontWeight="bold">
                {t("reportsPage.typeNotSelected")}
              </Text>
            ) : (
              <SelectCategory handleSetCategory={handleSetCategory} />
            );
      }
    }

    return (
      <Page title={t(`${translationPath}.title`)} subTitle={t(`${translationPath}.subTitle`)}>
        <Card>
          <AssigneeHeader
            assignee={
              assignee &&
              questionnaireData &&
              assignee?.name?.toLowerCase() !== questionnaireData.assigneeName?.toLowerCase()
                ? { name: questionnaireData.assigneeName }
                : assignee
            }
            period={questionnaireData.period}
            customComponent={
              <ReturnToList
                onClickHandler={handleReturnToListClick}
                title={
                  questionnaireData.status !== QUESTIONNAIRE_STATUSES.FINALIZED && questionnaireData.category !== null
                    ? t("returnToListTooltip")
                    : null
                }
              />
            }
            customRightAlignedComponent={
              // Questionnaire type must be Emission Based
              questionnaireData?.questionnaireType === SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED &&
              // Button must be visible only for the assignee (Supplier Editor) and the admins (Org Admin and Supplier Admin)
              ((questionnaireData?.assigneeName?.toLowerCase() === user.email?.toLowerCase() &&
                userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER)) ||
                isAllowedToView(userPermissions, [USER_ROLES.ORG_ADMIN, USER_ROLES.SUPPLIER_ADMIN])) &&
              // Button must be only shown if the category is already selected
              typeof questionnaireData?.category !== "undefined" &&
              questionnaireData?.category !== null ? (
                <Button
                  colorScheme="white"
                  color="esg_lightgreen"
                  border={"1px solid"}
                  borderColor={"esg_lightgreen"}
                  fontWeight="bold"
                  variant="outline"
                  title={t("reportsPage.getBackBtnTooltip")}
                  onClick={() => onGetBackDialogOpen()}
                >
                  {t("reportsPage.getBackBtn")}
                </Button>
              ) : null
            }
          />
          {categoryComponent}
          <ConfirmationDialog
            isOpen={isGetBackDialogOpen}
            onClose={() => onGetBackDialogClose()}
            onConfirm={handleCategorySwitch}
            headerText={t("reportsPage.getBackDialogHeader")}
            confirmationText={t("reportsPage.getBackQuestion")}
          />
        </Card>
      </Page>
    );
  };

  if ((error && error.response.status === 404) || (!isLoading && !isFetching && typeof data === "undefined")) {
    return (
      <Flex blockSize="full">
        <Center flex="1">
          <Box textAlign="center">
            {error && error.response.status === 404 ? (
              <>
                <Tag size="lg">{t("noAvailableQuestionnaire")}</Tag>
                <Box>
                  <ReturnToList onClickHandler={() => navigate(getSupplierQuestionnairesRoute(userPermissions))} />
                </Box>
              </>
            ) : (
              <Tag size="lg">{t("somethingWentWrong")}</Tag>
            )}
          </Box>
        </Center>
      </Flex>
    );
  } else {
    return questionnaireData && typeof questionnaireData !== "undefined" ? renderCategoryComponent() : null;
  }
}

export default WithAuthorization(SupplierQuestionnaire);
